const DATABASE_NAME = "nexus-ledger-outbox";
const DATABASE_VERSION = 1;
const STORE_NAME = "pending-ledger";

const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/u;
const EVENT_TYPE_PATTERN = /^[a-z][a-z0-9.-]{0,63}$/u;
const MESSAGE_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/u;
const SWARM_CODE_PATTERN = /^[A-Z0-9]{8}$/u;

export interface EncryptedLedgerEnvelope {
  /** Routing identifier. It is not part of the encrypted payload. */
  swarmCode: string;
  eventType: string;
  messageId: string;
  /** AES-GCM ciphertext encoded as unpadded base64url. */
  ciphertext: string;
  /** 12-byte AES-GCM IV encoded as unpadded base64url (16 characters). */
  iv: string;
}

export interface LedgerOutboxRecord extends EncryptedLedgerEnvelope {
  id: string;
  createdAt: number;
  expiresAt: number;
  nextAttemptAt: number;
  attempts: number;
  byteSize: number;
}

export interface LedgerOutboxPolicy {
  maxItems: number;
  maxItemBytes: number;
  maxTotalBytes: number;
  ttlMs: number;
  flushBatchSize: number;
  requestTimeoutMs: number;
  baseRetryMs: number;
  maxRetryMs: number;
}

export const DEFAULT_LEDGER_OUTBOX_POLICY: Readonly<LedgerOutboxPolicy> = Object.freeze({
  maxItems: 256,
  maxItemBytes: 384 * 1024,
  maxTotalBytes: 8 * 1024 * 1024,
  ttlMs: 7 * 24 * 60 * 60 * 1000,
  flushBatchSize: 24,
  requestTimeoutMs: 15_000,
  baseRetryMs: 1_000,
  maxRetryMs: 60 * 60 * 1000,
});

export interface EnqueueResult {
  queued: boolean;
  duplicate: boolean;
  evicted: number;
}

export interface LedgerOutboxStats {
  count: number;
  bytes: number;
  oldestCreatedAt?: number;
}

export interface FlushResult {
  sent: number;
  dropped: number;
  deferred: number;
  remaining: number;
}

export type LedgerDeliveryResult =
  | { state: "sent"; status: number }
  | { state: "queued"; status?: number; enqueue: EnqueueResult };

export interface LedgerOutboxStore {
  enqueue(
    record: LedgerOutboxRecord,
    policy: Readonly<LedgerOutboxPolicy>,
    now: number,
  ): Promise<EnqueueResult>;
  listDue(now: number, limit: number): Promise<LedgerOutboxRecord[]>;
  markRetry(id: string, attempts: number, nextAttemptAt: number): Promise<void>;
  remove(id: string): Promise<void>;
  purgeExpired(now: number): Promise<number>;
  stats(now: number): Promise<LedgerOutboxStats>;
  clear(): Promise<void>;
}

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface LedgerOutboxOptions {
  store?: LedgerOutboxStore;
  policy?: Partial<LedgerOutboxPolicy>;
  fetcher?: Fetcher;
  now?: () => number;
  random?: () => number;
  isOnline?: () => boolean;
}

export interface AutoFlushOptions {
  eventTarget?: Pick<EventTarget, "addEventListener" | "removeEventListener">;
  onError?: (error: unknown) => void;
}

function requestResult<T>(request: IDBRequest<T>, fallbackMessage: string): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () => reject(request.error ?? new Error(fallbackMessage)));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve());
    transaction.addEventListener("abort", () => {
      reject(transaction.error ?? new Error("Ledger outbox transaction was aborted"));
    });
    transaction.addEventListener("error", () => {
      reject(transaction.error ?? new Error("Ledger outbox transaction failed"));
    });
  });
}

function openDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB is unavailable; the ledger event cannot be queued"));
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("createdAt", "createdAt");
        store.createIndex("expiresAt", "expiresAt");
        store.createIndex("nextAttemptAt", "nextAttemptAt");
      }
    });
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () => {
      reject(request.error ?? new Error("Ledger outbox database could not be opened"));
    });
    request.addEventListener("blocked", () => {
      reject(new Error("Ledger outbox database upgrade was blocked"));
    });
  });
}

/** IndexedDB implementation. A separate database avoids version conflicts with chat history. */
export class IndexedDbLedgerOutboxStore implements LedgerOutboxStore {
  async enqueue(
    record: LedgerOutboxRecord,
    policy: Readonly<LedgerOutboxPolicy>,
    now: number,
  ): Promise<EnqueueResult> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const allRequest = store.getAll() as IDBRequest<LedgerOutboxRecord[]>;
      let result: EnqueueResult = { queued: false, duplicate: false, evicted: 0 };
      allRequest.addEventListener("success", () => {
        const current = allRequest.result;
        const live: LedgerOutboxRecord[] = [];
        for (const item of current) {
          if (item.expiresAt <= now) store.delete(item.id);
          else live.push(item);
        }
        if (live.some((item) => item.id === record.id)) {
          result = { queued: false, duplicate: true, evicted: 0 };
          return;
        }

        live.sort((left, right) => left.createdAt - right.createdAt || left.id.localeCompare(right.id));
        let totalBytes = live.reduce((total, item) => total + item.byteSize, 0);
        while (
          live.length >= policy.maxItems
          || (live.length > 0 && totalBytes + record.byteSize > policy.maxTotalBytes)
        ) {
          const evicted = live.shift();
          if (!evicted) break;
          store.delete(evicted.id);
          totalBytes -= evicted.byteSize;
          result.evicted += 1;
        }
        store.put(record);
        result.queued = true;
      });
      const requestCompletion = requestResult(allRequest, "Ledger outbox could not be read");
      await Promise.all([requestCompletion, transactionDone(transaction)]);
      return result;
    } finally {
      database.close();
    }
  }

  async listDue(now: number, limit: number): Promise<LedgerOutboxRecord[]> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(STORE_NAME, "readonly");
      const request = transaction.objectStore(STORE_NAME).getAll() as IDBRequest<LedgerOutboxRecord[]>;
      const records = await requestResult(request, "Ledger outbox could not be read");
      await transactionDone(transaction);
      return records
        .filter((record) => record.expiresAt > now && record.nextAttemptAt <= now)
        .sort((left, right) => left.nextAttemptAt - right.nextAttemptAt
          || left.createdAt - right.createdAt
          || left.id.localeCompare(right.id))
        .slice(0, limit);
    } finally {
      database.close();
    }
  }

  async markRetry(id: string, attempts: number, nextAttemptAt: number): Promise<void> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id) as IDBRequest<LedgerOutboxRecord | undefined>;
      request.addEventListener("success", () => {
        if (request.result) store.put({ ...request.result, attempts, nextAttemptAt });
      });
      await Promise.all([
        requestResult(request, "Ledger outbox retry state could not be read"),
        transactionDone(transaction),
      ]);
    } finally {
      database.close();
    }
  }

  async remove(id: string): Promise<void> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).delete(id);
      await transactionDone(transaction);
    } finally {
      database.close();
    }
  }

  async purgeExpired(now: number): Promise<number> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll() as IDBRequest<LedgerOutboxRecord[]>;
      let purged = 0;
      request.addEventListener("success", () => {
        for (const record of request.result) {
          if (record.expiresAt <= now) {
            store.delete(record.id);
            purged += 1;
          }
        }
      });
      await Promise.all([
        requestResult(request, "Ledger outbox could not be pruned"),
        transactionDone(transaction),
      ]);
      return purged;
    } finally {
      database.close();
    }
  }

  async stats(now: number): Promise<LedgerOutboxStats> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(STORE_NAME, "readonly");
      const request = transaction.objectStore(STORE_NAME).getAll() as IDBRequest<LedgerOutboxRecord[]>;
      const records = (await requestResult(request, "Ledger outbox statistics could not be read"))
        .filter((record) => record.expiresAt > now);
      await transactionDone(transaction);
      const oldest = records.reduce<number | undefined>(
        (value, record) => value === undefined ? record.createdAt : Math.min(value, record.createdAt),
        undefined,
      );
      return {
        count: records.length,
        bytes: records.reduce((total, record) => total + record.byteSize, 0),
        ...(oldest === undefined ? {} : { oldestCreatedAt: oldest }),
      };
    } finally {
      database.close();
    }
  }

  async clear(): Promise<void> {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).clear();
      await transactionDone(transaction);
    } finally {
      database.close();
    }
  }
}

function normalizedPolicy(overrides: Partial<LedgerOutboxPolicy> = {}): LedgerOutboxPolicy {
  const policy = { ...DEFAULT_LEDGER_OUTBOX_POLICY, ...overrides };
  for (const [name, value] of Object.entries(policy)) {
    if (!Number.isSafeInteger(value) || value <= 0) {
      throw new Error(`Ledger outbox policy ${name} must be a positive safe integer`);
    }
  }
  if (policy.maxItemBytes > policy.maxTotalBytes) {
    throw new Error("Ledger outbox maxItemBytes cannot exceed maxTotalBytes");
  }
  return policy;
}

function assertEnvelope(value: EncryptedLedgerEnvelope): void {
  if (!SWARM_CODE_PATTERN.test(value.swarmCode)) throw new Error("Invalid swarm code for ledger outbox");
  if (!EVENT_TYPE_PATTERN.test(value.eventType)) throw new Error("Invalid event type for ledger outbox");
  if (!MESSAGE_ID_PATTERN.test(value.messageId)) throw new Error("Invalid message ID for ledger outbox");
  if (
    value.ciphertext.length === 0
    || value.ciphertext.length > 350_000
    || value.ciphertext.length % 4 === 1
    || !BASE64URL_PATTERN.test(value.ciphertext)
  ) {
    throw new Error("Invalid ciphertext for ledger outbox");
  }
  if (value.iv.length !== 16 || !BASE64URL_PATTERN.test(value.iv)) {
    throw new Error("Invalid IV for ledger outbox");
  }
}

function encodedRecordBytes(value: EncryptedLedgerEnvelope): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

function retryAfterMs(response: Response, now: number): number | undefined {
  const value = response.headers.get("Retry-After");
  if (!value) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds * 1000);
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - now) : undefined;
}

function isPermanentFailure(status: number): boolean {
  return status === 400
    || status === 401
    || status === 403
    || status === 404
    || status === 410
    || status === 413
    || status === 422;
}

export class LedgerOutbox {
  readonly policy: Readonly<LedgerOutboxPolicy>;
  private readonly store: LedgerOutboxStore;
  private readonly fetcher: Fetcher;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly isOnline: () => boolean;
  private activeFlush: Promise<FlushResult> | null = null;

  constructor(options: LedgerOutboxOptions = {}) {
    this.policy = Object.freeze(normalizedPolicy(options.policy));
    this.store = options.store ?? new IndexedDbLedgerOutboxStore();
    this.fetcher = options.fetcher ?? ((input, init) => fetch(input, init));
    this.now = options.now ?? (() => Date.now());
    this.random = options.random ?? (() => Math.random());
    this.isOnline = options.isOnline ?? (() => typeof navigator === "undefined" || navigator.onLine);
  }

  async enqueue(envelope: EncryptedLedgerEnvelope): Promise<EnqueueResult> {
    assertEnvelope(envelope);
    const now = this.now();
    const byteSize = encodedRecordBytes(envelope);
    if (byteSize > this.policy.maxItemBytes) {
      throw new Error("Encrypted ledger envelope exceeds the offline outbox item limit");
    }
    const record: LedgerOutboxRecord = {
      ...envelope,
      id: `${envelope.swarmCode}:${envelope.messageId}`,
      createdAt: now,
      expiresAt: now + this.policy.ttlMs,
      nextAttemptAt: now,
      attempts: 0,
      byteSize,
    };
    return this.store.enqueue(record, this.policy, now);
  }

  async postOrEnqueue(envelope: EncryptedLedgerEnvelope): Promise<LedgerDeliveryResult> {
    assertEnvelope(envelope);
    if (this.isOnline()) {
      try {
        const response = await this.post(envelope);
        if (response.ok) return { state: "sent", status: response.status };
        return {
          state: "queued",
          status: response.status,
          enqueue: await this.enqueue(envelope),
        };
      } catch {
        // The encrypted envelope is persisted below; plaintext is never accepted by this module.
      }
    }
    return { state: "queued", enqueue: await this.enqueue(envelope) };
  }

  flush(): Promise<FlushResult> {
    if (this.activeFlush) return this.activeFlush;
    const pending = this.flushInternal();
    this.activeFlush = pending;
    void pending.finally(() => {
      if (this.activeFlush === pending) this.activeFlush = null;
    }).catch(() => undefined);
    return pending;
  }

  private async flushInternal(): Promise<FlushResult> {
    const now = this.now();
    const droppedExpired = await this.store.purgeExpired(now);
    if (!this.isOnline()) {
      const remaining = (await this.store.stats(now)).count;
      return { sent: 0, dropped: droppedExpired, deferred: remaining, remaining };
    }

    const records = await this.store.listDue(now, this.policy.flushBatchSize);
    let sent = 0;
    let dropped = droppedExpired;
    let deferred = 0;
    for (const record of records) {
      try {
        const response = await this.post(record);
        if (response.ok) {
          await this.store.remove(record.id);
          sent += 1;
          continue;
        }
        if (isPermanentFailure(response.status)) {
          await this.store.remove(record.id);
          dropped += 1;
          continue;
        }
        await this.defer(record, retryAfterMs(response, this.now()));
        deferred += 1;
        break;
      } catch {
        await this.defer(record);
        deferred += 1;
        break;
      }
    }
    const remaining = (await this.store.stats(this.now())).count;
    return { sent, dropped, deferred, remaining };
  }

  private async post(envelope: EncryptedLedgerEnvelope): Promise<Response> {
    const controller = new AbortController();
    const timeout = globalThis.setTimeout(() => controller.abort(), this.policy.requestTimeoutMs);
    try {
      return await this.fetcher(`/api/swarms/${envelope.swarmCode}/ledger`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eventType: envelope.eventType,
          messageId: envelope.messageId,
          ciphertext: envelope.ciphertext,
          iv: envelope.iv,
        }),
        signal: controller.signal,
      });
    } finally {
      globalThis.clearTimeout(timeout);
    }
  }

  private async defer(record: LedgerOutboxRecord, serverDelayMs?: number): Promise<void> {
    const attempts = Math.min(record.attempts + 1, 31);
    const exponential = Math.min(
      this.policy.maxRetryMs,
      this.policy.baseRetryMs * (2 ** Math.min(attempts - 1, 20)),
    );
    const jittered = Math.round(exponential * (1 + Math.max(0, Math.min(1, this.random())) * 0.25));
    const delay = Math.min(
      this.policy.maxRetryMs,
      Math.max(jittered, serverDelayMs ?? 0),
    );
    await this.store.markRetry(record.id, attempts, this.now() + delay);
  }

  async stats(): Promise<LedgerOutboxStats> {
    const now = this.now();
    await this.store.purgeExpired(now);
    return this.store.stats(now);
  }

  clear(): Promise<void> {
    return this.store.clear();
  }

  /** Flushes once at startup and whenever the browser reports that it is online again. */
  startAutoFlush(options: AutoFlushOptions = {}): () => void {
    const target = options.eventTarget
      ?? (typeof window === "undefined" ? undefined : window);
    const trigger = (): void => {
      void this.flush().catch((error: unknown) => options.onError?.(error));
    };
    target?.addEventListener("online", trigger);
    queueMicrotask(trigger);
    return () => target?.removeEventListener("online", trigger);
  }
}

export const ledgerOutbox = new LedgerOutbox();

export function postLedgerOrQueue(
  envelope: EncryptedLedgerEnvelope,
): Promise<LedgerDeliveryResult> {
  return ledgerOutbox.postOrEnqueue(envelope);
}

export function flushLedgerOutbox(): Promise<FlushResult> {
  return ledgerOutbox.flush();
}

export function startLedgerOutboxAutoFlush(options?: AutoFlushOptions): () => void {
  return ledgerOutbox.startAutoFlush(options);
}
